// ErrorPage.jsx
import React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { AlertCircle, ArrowLeft, Home, Search } from 'lucide-react'

const ErrorPage = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { message, searchTerm, redirectTo } = location.state || {}

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-orange-50 to-yellow-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="max-w-md w-full bg-white rounded-2xl shadow-2xl overflow-hidden"
      >
        {/* Animated header */}
        <div className="bg-gradient-to-r from-red-500 to-orange-500 p-6 text-center">
          <motion.div
            animate={{ 
              rotate: [0, -10, 10, -5, 5, 0],
              scale: [1, 1.1, 1]
            }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <AlertCircle className="w-20 h-20 text-white mx-auto" />
          </motion.div>
          <motion.h1
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-3xl font-bold text-white mt-4"
          >
            Invalid Search
          </motion.h1>
        </div>

        <div className="p-6">
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            <p className="text-gray-800 text-lg mb-2">
              {message || "This search term is not bird-related."}
            </p>
            {searchTerm && (
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
                <p className="text-yellow-700 font-medium">
                  You searched for: <span className="font-bold">"{searchTerm}"</span>
                </p>
                <p className="text-yellow-600 text-sm mt-1">
                  Please search for bird-related terms only.
                </p>
              </div>
            )}
          </motion.div>

          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="bg-green-50 p-4 rounded-lg mb-6"
          >
            <p className="text-green-800 font-semibold mb-2">Suggested bird searches:</p>
            <div className="flex flex-wrap gap-2">
              {['eagle', 'parrot', 'hummingbird', 'owl', 'peacock', 'penguin', 'flamingo', 'sparrow'].map((suggestion) => (
                <button
                  key={suggestion}
                  onClick={() => navigate('/gallery', { state: { searchTerm: suggestion } })}
                  className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm hover:bg-green-200 transition"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="space-y-3"
          >
            <button
              onClick={() => navigate(-1)}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition font-medium"
            >
              <ArrowLeft size={20} />
              Go Back
            </button>
            
            <button
              onClick={() => navigate('/gallery')}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg hover:from-green-600 hover:to-emerald-600 transition font-medium shadow-md"
            >
              <Home size={20} />
              Return to Bird Gallery
            </button>
          </motion.div>
        </div>
      </motion.div>
    </div>
  )
}

export default ErrorPage